/**
 * Team Review magic-link page — pt.87 (2026-09-18).
 *
 * Public, auth-less page opened from the email link the tech / CSR
 * receives during the pay-week review window (4h). Renders the exact
 * snapshot the person is approving; posts approve or approve_with_notes.
 *
 * URL: /team-review/{token}
 * Backend: GET /api/team-review/{token}    — resolve the review
 *          POST /api/team-review/{token}/respond {decision, notes?}
 *
 * Handles the terminal states from the API cleanly:
 *   404 → link not found (already invalidated by recompute, or garbage token)
 *   410 → window closed (4h) OR review invalidated
 *   409 → already responded (single-use)
 *
 * Mobile-first: single column, big touch targets, no dashboard chrome.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CSR_JOB_HDR, csrJobCells, money as fmtMoney, jobLineMoneyCells, showTfs, overageView, overageLineText, overageMismatchText, OVERAGE_LABEL } from "@/lib/payrollView";
import {
  Loader2, CheckCircle2, AlertTriangle, MessageSquare,
  Clock, ThumbsUp, ChevronDown, ChevronUp,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const money = (v) => (v == null ? "—"
  : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (v) => (v == null ? "—" : `${(Number(v) * 100).toFixed(1)}%`);

export default function TeamReviewMagicLink() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);          // { status, detail }
  const [review, setReview] = useState(null);
  const [done, setDone] = useState(null);            // POST response body
  const [showNotesBox, setShowNotesBox] = useState(false);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const { data } = await axios.get(`${BACKEND}/api/team-review/${encodeURIComponent(token)}`);
        if (cancel) return;
        // pt.87 — if the review is already terminal (approved / approved_with_notes),
        // skip straight to the success card. Backend returns 410 for invalidated
        // and expired states, so those hit the catch block and render as errors.
        if (data.status === "approved" || data.status === "approved_with_notes") {
          setReview(data);
          setDone({ status: data.status, responded_at: data.responded_at });
        } else {
          setReview(data);
        }
      } catch (e) {
        if (!cancel) setError({
          status: e?.response?.status,
          detail: e?.response?.data?.detail || e.message,
        });
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [token]);

  const submit = async (decision) => {
    if (submitting) return;
    if (decision === "approved_with_notes" && !notes.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${BACKEND}/api/team-review/${encodeURIComponent(token)}/respond`,
        { decision, notes: decision === "approved_with_notes" ? notes.trim() : null },
      );
      setDone(data);
    } catch (e) {
      setError({
        status: e?.response?.status,
        detail: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ------------------------------------------------------------- shells
  if (loading) return (
    <PageShell>
      <div className="flex items-center gap-2 text-[color:var(--cc-ink-muted)] text-[13.5px]" data-testid="team-review-loading">
        <Loader2 size={16} className="animate-spin" /> Loading your review…
      </div>
    </PageShell>
  );

  if (error) return (
    <PageShell>
      <ErrorCard status={error.status} detail={error.detail} />
    </PageShell>
  );

  if (done) return (
    <PageShell>
      <SuccessCard done={done} subjectName={review?.subject_name} />
    </PageShell>
  );

  if (!review) return null;

  // ------------------------------------------------------------- body
  const isTech = review.subject_type === "tech";
  const snap = review.snapshot || {};
  const pw = review.pay_week || {};

  return (
    <PageShell>
      <div className="space-y-4" data-testid="team-review-body">

        <header className="text-center pt-2">
          <div className="text-[10.5px] uppercase tracking-[.14em] text-[color:var(--cc-ink-muted)] font-bold">
            Preliminary Pay Review
          </div>
          <h1
            className="font-display font-bold text-[24px] sm:text-[28px] tracking-tight text-[#0e1420] mt-1"
            data-testid="team-review-subject-name"
          >
            {review.subject_name}
          </h1>
          <div className="text-[13px] text-[color:var(--cc-ink-muted)] font-mono-data mt-0.5" data-testid="team-review-pay-week">
            Pay week {pw.monday} → {pw.sunday}
          </div>
          <ExpiresBadge expiresAt={review.expires_at} />
        </header>
        {review.is_preview && (
          <div className="bg-[#eef1f6] border border-[#dfe2e8] rounded-xl p-3 text-[12.5px] text-[#0e1420]" data-testid="team-review-preview-banner">
            TEST PREVIEW — this is a copy for the payroll editor. Responding here does not change {review.subject_name}&apos;s real review.
          </div>
        )}

        {/* PRELIMINARY disclaimer */}
        <div className="bg-[#fef3d9] border border-[#e6c99a] rounded-xl p-3.5 text-[12.5px] text-[#8a5a1a] flex gap-2" data-testid="team-review-disclaimer">
          <AlertTriangle size={15} className="shrink-0 mt-0.5" />
          <div>
            <b>Preliminary — not payroll of record.</b> Approving here confirms the numbers below.
            Final payroll is still pending your approver's sign-off.
          </div>
        </div>

        {/* Snapshot card — tech vs csr */}
        {isTech ? (
          <TechSnapshotCard snap={snap} />
        ) : (
          <CsrSnapshotCard snap={snap} name={review.subject_name} />
        )}

        {/* Raw snapshot for verification */}
        <button
          onClick={() => setShowRaw((v) => !v)}
          data-testid="team-review-raw-toggle"
          className="flex items-center gap-1.5 text-[12px] text-[color:var(--cc-ink-muted)] font-semibold hover:text-[#0072bc]"
        >
          {showRaw ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          {showRaw ? "Hide raw numbers" : "Show raw numbers"}
        </button>
        {showRaw && (
          <pre className="bg-[#f9fafb] border border-[#e7e9ee] rounded-lg p-3 text-[11px] font-mono-data overflow-auto max-h-[300px]" data-testid="team-review-raw-json">
            {JSON.stringify(snap, null, 2)}
          </pre>
        )}

        {/* Actions */}
        <div className="pt-2 space-y-2.5">
          <button
            onClick={() => submit("approved")}
            disabled={submitting}
            data-testid="team-review-approve-btn"
            className="w-full bg-[#0e8a68] text-white text-[15px] font-semibold px-4 py-3.5 rounded-xl hover:bg-[#0a6f53] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
          >
            <ThumbsUp size={17} /> {submitting ? "Submitting…" : "These numbers look right — Approve"}
          </button>

          {!showNotesBox && (
            <button
              onClick={() => setShowNotesBox(true)}
              disabled={submitting}
              data-testid="team-review-open-notes-btn"
              className="w-full bg-white border border-[#c96a1a] text-[#c96a1a] text-[14px] font-semibold px-4 py-3 rounded-xl hover:bg-[#fef8ea] flex items-center justify-center gap-2"
            >
              <MessageSquare size={15} /> Approve with a note
            </button>
          )}

          {showNotesBox && (
            <div className="border border-[#c96a1a] rounded-xl p-3 space-y-2 bg-[#fef8ea]" data-testid="team-review-notes-box">
              <label className="text-[12.5px] font-semibold text-[#8a5a1a]">
                Note for your approver (required)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="team-review-notes-input"
                placeholder="e.g. Job 37076 payment applied last week — please check materials share."
                rows={4}
                className="w-full border border-[#e6c99a] rounded-lg p-2.5 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-[#c96a1a] focus:ring-offset-1"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => submit("approved_with_notes")}
                  disabled={submitting || !notes.trim()}
                  data-testid="team-review-submit-notes-btn"
                  className="flex-1 bg-[#c96a1a] text-white text-[14px] font-semibold px-3 py-2.5 rounded-lg hover:bg-[#a55214] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Submitting…" : "Submit note & approve"}
                </button>
                <button
                  onClick={() => { setShowNotesBox(false); setNotes(""); }}
                  disabled={submitting}
                  data-testid="team-review-cancel-notes-btn"
                  className="px-3 py-2.5 text-[13px] font-semibold text-[color:var(--cc-ink-muted)] hover:text-[#0e1420]"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <footer className="pt-3 text-center text-[11.5px] text-[color:var(--cc-ink-muted)]" data-testid="team-review-footer">
          No response in 4 hours = auto-released to the approver. This link is single-use.
        </footer>
      </div>
    </PageShell>
  );
}

// =========================================================================
// Presentational
// =========================================================================
function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-[#f5f6f8] py-6 px-4" data-testid="team-review-page">
      <div className="max-w-[560px] mx-auto bg-white rounded-2xl shadow-md p-5 sm:p-7 border border-[#e7e9ee]">
        {children}
      </div>
    </div>
  );
}

function ExpiresBadge({ expiresAt }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => clearInterval(t);
  }, []);
  if (!expiresAt) return null;
  const exp = new Date(expiresAt);
  const ms = exp - now;
  if (ms <= 0) return (
    <div className="inline-flex items-center gap-1 mt-2 text-[11.5px] font-semibold text-[#7a1c1c] bg-[#fdecec] border border-[#f7c1c1] px-2 py-1 rounded" data-testid="team-review-expiry-badge">
      <Clock size={11} /> Review window closed
    </div>
  );
  const mins = Math.floor(ms / 60000);
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  const label = hrs > 0 ? `${hrs}h ${rem}m` : `${mins}m`;
  return (
    <div className="inline-flex items-center gap-1 mt-2 text-[11.5px] font-semibold text-[#1c6a99] bg-[#e7f4fb] border border-[#c2dff0] px-2 py-1 rounded" data-testid="team-review-expiry-badge">
      <Clock size={11} /> {label} left to respond
    </div>
  );
}

function TechSnapshotCard({ snap }) {
  return (
    <div className="border border-[#e7e9ee] rounded-xl overflow-hidden" data-testid="team-review-tech-snapshot">
      <div className="bg-[#f5f6f8] px-4 py-2.5 text-[11px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-semibold">
        Your week — line items
      </div>
      <div className="p-4 space-y-2 text-[14px]">
        <Row label="Weekly revenue"       value={money(snap.weekly_revenue)}     testid="team-review-tech-weekly-revenue" />
        <Row label="Commission (gross)"   value={money(snap.commission_gross)}   testid="team-review-tech-commission-gross" />
        {snap.weekly_tfs != null && <Row label="TFS deduct" value={money(snap.weekly_tfs)} />}
        {snap.cc_fee_dollars != null && <Row label="CC fee deduct" value={money(snap.cc_fee_dollars)} />}
        {snap.weekly_permits != null && <Row label="Permits deduct" value={money(snap.weekly_permits)} />}
        {snap.weekly_surges != null && <Row label="Surges deduct" value={money(snap.weekly_surges)} />}
        {snap.weekly_materials != null && <Row label="Materials deduct" value={money(snap.weekly_materials)} />}
        {snap.tier_pct != null && <Row label="Tier %" value={pct(snap.tier_pct)} />}
        {snap.min_wage_supplement != null && (
          <Row label={`Minimum-wage supplement (${Number(snap.min_wage?.paid_hours || 0).toFixed(2)}h × ${money(snap.min_wage?.rate)})`}
               value={money(snap.min_wage_supplement)} testid="team-review-tech-min-wage" />
        )}
        {snap.min_wage_balance && (
          <>
            <Row label="Minimum-wage balance (opening)" value={money(snap.min_wage_balance.opening)} testid="team-review-tech-mw-opening" />
            <Row label="Recovered this week" value={money(snap.min_wage_balance.recovery ? -snap.min_wage_balance.recovery : 0)} testid="team-review-tech-mw-recovery" />
            <Row label="Remaining balance" value={money(snap.min_wage_balance.remaining)} testid="team-review-tech-mw-remaining" />
          </>
        )}
      </div>
      <TechJobRows snap={snap} />
      <div className="px-4 py-3 border-t border-[#eef1f6] bg-[#e6f9f2] text-[#0e6b52] flex items-center justify-between">
        <span className="font-semibold text-[13px] uppercase tracking-wide">Final commission</span>
        <span className="font-mono-data font-bold text-[20px]" data-testid="team-review-tech-final-commission">
          {money(snap.final_commission)}
        </span>
      </div>
    </div>
  );
}

// CC-268: job rows (dollars only) + the material overage deduction line; rows + line = final commission.
function TechJobRows({ snap }) {
  const { jobs, line } = overageView(snap);
  if (!jobs.length) return null;
  const tfs = showTfs(jobs, "job_tfs");
  const hdr = jobLineMoneyCells({}, tfs).map(([label]) => label);
  return (
    <div className="border-t border-[#eef1f6] overflow-x-auto" data-testid="team-review-tech-jobs">
      <table className="w-full text-[12px]">
        <thead className="text-[10px] uppercase tracking-[.08em] text-[color:var(--cc-ink-muted)] bg-[#f9fafb]">
          <tr>
            <th className="text-left py-1.5 px-2">Job #</th>
            {hdr.map((h) => <th key={h} className="text-right py-1.5 px-2">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => (
            <tr key={j.job_number} className="border-t border-[#eef1f6]" data-testid={`team-review-tech-job-${j.job_number}`}>
              <td className="py-1.5 px-2 font-mono-data font-semibold">{j.job_number}</td>
              {jobLineMoneyCells(j, tfs).map(([h, v]) => <td key={h} className="py-1.5 px-2 text-right font-mono-data">{v}</td>)}
            </tr>
          ))}
          {line && (
            <tr className="border-t-2 border-[#e6c99a] bg-[#fef8ea] text-[#8a5a1a]" data-testid="team-review-tech-overage-line">
              <td className="py-1.5 px-2 font-semibold">{OVERAGE_LABEL}</td>
              <td className="py-1.5 px-2 text-[11px] font-mono-data" colSpan={hdr.length - 1} data-testid="team-review-tech-overage-detail">{overageLineText(line)}</td>
              <td className="py-1.5 px-2 text-right font-mono-data font-bold" data-testid="team-review-tech-overage-amount">{fmtMoney(line.commission_removed)}</td>
            </tr>
          )}
          {line?.mismatch != null && (
            <tr className="bg-[#fdecec]" data-testid="team-review-tech-overage-mismatch">
              <td colSpan={hdr.length + 1} className="py-1.5 px-2 text-[11.5px] font-semibold text-[#7a1c1c]">{overageMismatchText(line)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function CsrSnapshotCard({ snap, name }) {
  const share = snap.share || {};
  const pool = snap.pool_math || {};
  const breakdown = pool.per_csr_breakdown || [];
  return (
    <div className="border border-[#e7e9ee] rounded-xl overflow-hidden" data-testid="team-review-csr-snapshot">
      <div className="bg-[#f5f6f8] px-4 py-2.5 text-[11px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-semibold">
        Your share — CSR commission pool
      </div>
      <div className="p-4 space-y-2 text-[14px]">
        <Row label="Pool total this week" value={money(pool.total_dollars)} testid="team-review-csr-pool-total" />
        <Row label="Split method" value={pool.split_method || "—"} />
        <Row label="Your share %" value={pct(share.share_pct)} />
      </div>
      <div className="px-4 py-3 border-t border-[#eef1f6] bg-[#e6f9f2] text-[#0e6b52] flex items-center justify-between">
        <span className="font-semibold text-[13px] uppercase tracking-wide">Your share</span>
        <span className="font-mono-data font-bold text-[20px]" data-testid="team-review-csr-share-dollars">
          {money(share.share_dollars)}
        </span>
      </div>
      {breakdown.length > 0 && (
        <div className="border-t border-[#eef1f6]" data-testid="team-review-csr-breakdown">
          <div className="px-4 py-2 text-[10.5px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-semibold bg-[#f9fafb]">
            Full pool breakdown
          </div>
          <table className="w-full text-[12.5px]">
            <tbody>
              {breakdown.map((b) => (
                <tr key={b.csr_profile_id} className="border-t border-[#eef1f6]">
                  <td className={`py-2 px-4 ${b.name === name ? "font-bold" : ""}`}>
                    {b.name}{b.name === name && <span className="ml-1 text-[10px] text-[#0e6b52]">(you)</span>}
                  </td>
                  <td className="py-2 px-4 text-right font-mono-data">{pct(b.share_pct)}</td>
                  <td className="py-2 px-4 text-right font-mono-data font-bold">{money(b.share_dollars)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(snap.jobs || []).length > 0 && (
        <div className="border-t border-[#eef1f6] overflow-x-auto" data-testid="team-review-csr-jobs">
          <div className="px-4 py-2 text-[10.5px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-semibold bg-[#f9fafb]">
            Jobs in this week's pool
          </div>
          <table className="w-full text-[12px]">
            <thead className="text-[10px] uppercase tracking-[.08em] text-[color:var(--cc-ink-muted)]">
              <tr>{[...CSR_JOB_HDR, "Your $"].map((h, i) => <th key={h} className={`py-1.5 px-2 ${i > 2 ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {snap.jobs.map((j) => (
                <tr key={j.job_number} className="border-t border-[#eef1f6]" data-testid={`team-review-csr-job-${j.job_number}`}>
                  {csrJobCells(j).map((c, i) => <td key={CSR_JOB_HDR[i]} className={`py-1.5 px-2 ${i > 2 ? "text-right font-mono-data" : ""}`}>{c}</td>)}
                  <td className="py-1.5 px-2 text-right font-mono-data font-bold">{fmtMoney(j.your_dollars)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[color:var(--cc-ink-muted)]">{label}</span>
      <span className="font-mono-data font-semibold text-[#0e1420]" data-testid={testid}>{value}</span>
    </div>
  );
}

function ErrorCard({ status, detail }) {
  let title = "Something went wrong";
  let subtitle = typeof detail === "string" ? detail : JSON.stringify(detail);
  let icon = <AlertTriangle size={22} className="text-[#7a1c1c]" />;
  if (status === 404) {
    title = "Link not found";
    subtitle = "This review link is no longer valid. It may have been replaced by a newer link after the numbers were updated. Check your email for the most recent link.";
  } else if (status === 410) {
    title = "Review window closed";
    subtitle = detail || "This review has been auto-released to your approver. No action needed on your end.";
    icon = <Clock size={22} className="text-[#7a1c1c]" />;
  } else if (status === 409) {
    title = "Already responded";
    subtitle = detail || "This link has already been used — one response per link.";
    icon = <CheckCircle2 size={22} className="text-[#8a5a1a]" />;
  }
  return (
    <div className="text-center py-4" data-testid="team-review-error">
      <div className="mx-auto w-11 h-11 rounded-full bg-[#fdecec] flex items-center justify-center mb-3">
        {icon}
      </div>
      <div className="font-display font-bold text-[18px] text-[#0e1420]" data-testid="team-review-error-title">{title}</div>
      <div className="text-[13px] text-[color:var(--cc-ink-muted)] mt-2 max-w-[420px] mx-auto" data-testid="team-review-error-detail">
        {subtitle}
      </div>
    </div>
  );
}

function SuccessCard({ done, subjectName }) {
  const isNotes = done.status === "approved_with_notes";
  return (
    <div className="text-center py-4" data-testid="team-review-success">
      <div className="mx-auto w-14 h-14 rounded-full bg-[#e6f9f2] flex items-center justify-center mb-3">
        <CheckCircle2 size={28} className="text-[#0e6b52]" />
      </div>
      <div className="font-display font-bold text-[20px] text-[#0e1420]">
        {isNotes ? "Note received — thanks!" : "Approved — thanks!"}
      </div>
      <div className="text-[13px] text-[color:var(--cc-ink-muted)] mt-2 max-w-[420px] mx-auto">
        {subjectName ? `${subjectName}, ` : ""}your approver will now review the payroll run for final sign-off. You'll only hear from us again if something changes and needs re-review.
      </div>
      <div className="mt-4 inline-flex items-center gap-2 bg-[#e6f9f2] text-[#0e6b52] px-3 py-1.5 rounded-full text-[11.5px] font-semibold">
        Status: <span className="font-mono-data">{done.status}</span>
      </div>
    </div>
  );
}
